/**
 * Represents the initial state of the application.
 * @typedef {Object} InitialState
 * @property {Object} SCREEN_STATE - Represents the screen state.
 * @property {Date|null} SCREEN_STATE.time - Represents the current time.
 * @property {number} SCREEN_STATE.selectedId - Represents the selected ID.
 * @property {Array<Object>} SCREEN_STATE.screens - Represents the screens.
 * @property {Object} SCREEN_STATE.trackInfo - Represents the track info.
 * @property {string} SCREEN_STATE.trackInfo.audio_track_name - Represents the audio track name.
 * @property {Object} SCREEN_STATE.currentScreen - Represents the current screen.
 * @property {number|null} SCREEN_STATE.currentScreen.id - Represents the ID of the current screen.
 * @property {string} SCREEN_STATE.currentScreen.state - Represents the state of the current screen.
 * @property {boolean} SCREEN_STATE.isChangeResMode - Represents if the resolution mode is being changed.
 * @property {Object} MEDIA_TRACKS - Represents the media tracks.
 * @property {Array<string>} MEDIA_TRACK_IDS - Represents the media track IDs.
 * @property {Object} SCREEN_SWITCH_STEP - Represents the screen switch step.
 * @property {string} SCREEN_SWITCH_STEP.step - Represents the step of the screen switch.
 * @property {Object|null} SCREEN_SWITCH_STEP.payload - Represents the payload of the screen switch.
 * @property {Object} GRID_STATE - Represents the grid state.
 * @property {boolean} GRID_STATE.isActive - Represents if the grid state is active.
 * @property {number} GRID_STATE.n - Represents the value of n.
 * @property {Object} GRID_STATE.cell - Represents the grid cell.
 * @property {number} GRID_STATE.cell.w - Represents the width of the grid cell.
 * @property {number} GRID_STATE.cell.h - Represents the height of the grid cell.
 * @property {Object} AUTO_JOIN - Represents the auto join state.
 * @property {boolean} AUTO_JOIN.debug - Represents if debug mode is enabled.
 * @property {boolean} AUTO_JOIN.loading - Represents if the auto join is loading.
 * @property {string|null} AUTO_JOIN.step - Represents the step of the auto join.
 * @property {string} AUTO_JOIN.state - Represents the state of the auto join.
 * @property {string} AUTO_JOIN.status - Represents the status of the auto join.
 * @property {boolean} AUTO_JOIN.player - Represents if the player is enabled.
 * @property {Object} MEETING_STATE - Represents the meeting state.
 * @property {string|null} MEETING_STATE.title - Represents the title of the meeting.
 * @property {Object} MEETING_STATE.currentUser - Represents the current user.
 * @property {Object[]} MEETING_STATE.users.all - Represents all the users.
 * @property {Object[]} MEETING_STATE.users.active - Represents the active users.
 * @property {string} MEETING_STATE.order - Represents the order of the meeting.
 * @property {string|null} MEETING_STATE.created_by - Represents the creator of the meeting.
 * @property {boolean|null} MEETING_STATE.consentDialog - Represents the user media permission consent dialog state. decided in janus videoroom plugin.
 * @property {Object|null} MEETING_STATE.webcamStream - Represents the webcam stream. It contains a single video track that is them same (by reference) video track that janus videoroom plugin maintains (creates & stops).
 * @property {Object|null} MEETING_STATE.micStream - Represents the microphone stream. It contains a single audio track that is them same (by reference) audio track that janus videoroom plugin maintains (creates & stops).
 * @property {Object|null} MEETING_STATE.meetingVideoStream - Represents the meeting video stream.
 * @property {Object|null} MEETING_STATE.meetingAudioStream - Represents the meeting audio stream.
 * @property {Object|null} MEETING_STATE.screenAudioStream - Represents the screen audio stream.
 * @property {Object} MEETING_STATE.lifecycle - Represents the lifecycle of the meeting.
 * @property {string|null} MEETING_STATE.activeMeetingId - Represents the ID of the active meeting.
 * @property {boolean} MEETING_STATE.switch - Represents if the meeting is being switched.
 */
export const global_initial = {
  SCREEN_STATE: {
    time: null,
    selectedId: 102,
    screens: [],
    trackInfo: {
      audio_track_name: "",
    },
    currentScreen: {
      id: null,
      state: "initializing",
    },
    isChangeResMode: false,
  },
  MINI_TRACKS: [],
  MEDIA_TRACKS: {},
  MEDIA_TRACK_IDS: [],
  SCREEN_SWITCH_STEP: {
    step: "WAITING",
    payload: null,
  },
  GRID_STATE: {
    isActive: false,
    n: 0,
    cell: {
      w: 0,
      h: 0,
    },
  },
  AUTO_JOIN: {
    debug: false,
    loading: false,
    step: null,
    state: "WAITING",
    status: "",
    player: false,
  },
  MEETING_STATE: {
    title: null,
    currentUser: {},
    users: {
      all: [],
      active: [],
    },
    order: "",
    created_by: null,
    consentDialog: null,
    webcamStream: null,
    micStream: null,
    meetingVideoStream: null,
    meetingAudioStream: null,
    screenAudioStream: null,
    lifecycle: {},
    activeMeetingId: null,
    switch: false,
  },
  MEETING_REF: {
    title: null,
    userCount: null,
    currentUser: {
      username: null,
      index: null,
      isAudioMuted: {
        local: true,
        server: false,
      },
      isVideoMuted: {
        local: true,
        server: false,
      },
    },
    users: [],
  },
  SCREEN_REF: {
    selectedId: 102,
    tracks: [],
  },
};
